import {
  Card,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Stack,
  useTheme,
} from "@mui/material";
import { CheckCircle, Schedule } from "@mui/icons-material";
import { formatTime } from "@pages/OrderTracker/components/PlanningAutomation/Timer";
import type { ActiveJobOut } from "@providers/hop-ord-server/api";

interface ActiveJobsTableProps {
  activeJobs: ActiveJobOut[];
}

export const ActiveJobsTable = ({ activeJobs }: ActiveJobsTableProps) => {
  const theme = useTheme();

  const getJobStatusIcon = (retries: number) => {
    if (retries > 1) return <Schedule color="warning" />;
    return <CheckCircle color="success" />;
  };

  return (
    <Card>
      <TableContainer
        sx={{
          maxHeight: "400px",
          overflow: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            "& .MuiTableCell-head": { padding: 2 },
            "& .MuiTableCell-body": {
              padding: 1,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiTableCell-root:last-child": {
              borderRight: 0,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Consumer</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Stream</TableCell>
              <TableCell align="right">Elapsed Time</TableCell>
              <TableCell align="right">Attempt</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Stack alignItems="center" gap={1} py={2}>
                    <Typography variant="body2" color="text.secondary">
                      No active jobs found
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              activeJobs.map((job, index) => (
                <TableRow
                  key={`${job.jobId || "unknown"}-${index}`}
                  sx={{
                    backgroundColor:
                      job.retryCount > 1
                        ? theme.palette.error.light
                        : "inherit",
                  }}
                >
                  <TableCell>{job.jobId}</TableCell>
                  <TableCell>{job.consumerName || "Unknown"}</TableCell>
                  <TableCell>{job.groupName}</TableCell>
                  <TableCell>{job.streamName}</TableCell>
                  <TableCell align="right">
                    {formatTime(Math.floor(job.elapsedTimeMs / 1000))}
                  </TableCell>
                  <TableCell align="right">
                    {job.retryCount > 1 ? (
                      <Typography color="error.main" fontWeight="bold">
                        {job.retryCount}
                      </Typography>
                    ) : (
                      job.retryCount
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {getJobStatusIcon(job.retryCount)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};
